import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getIndustries,
    getDefaultEmissionCategories,
    addDefaultEmissionCategory,
    deleteDefaultEmissionCategory,
    searchEmissionCategories,
    getCategoryFactors, addDefaultCategoryFactor, searchEmissionFactors, deleteDefaultCategoryFactor
} from "../processes/industry_category_factor/category_per_industry";

import EmissionFactorLine from "../components/EmissionFactorLine";

export default function DefaultIndustryCategoryFactor() {
    const [activeIndustryId, setActiveIndustryId] = useState(null);
    const [activeCategoryId, setActiveCategoryId] = useState(null);

    // Load all industries on mount
    const {
        data: industries = [],
        isLoading: loadingIndustries,
        isError: errorIndustries,
    } = useQuery({ queryKey: ["industries"], queryFn: getIndustries });

    return (
        <div style={{ padding: 16 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
                <colgroup>
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "37.5%" }} />
                    <col style={{ width: "37.5%" }} />
                </colgroup>
                <thead>
                    <tr>
                        <th style={th}>INDUSTRY</th>

                        {/* Category & Scope header with Add button */}
                        <th style={{ ...th, position: "relative" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                <span>CATEGORY &amp; SCOPE</span>
                                {/* Add control lives inside Category column header */}
                                <AddCategoryControls
                                    activeIndustryId={activeIndustryId}
                                    disabled={!activeIndustryId}
                                />
                            </div>
                        </th>

                        <th style={{ ...th, position: "relative" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                <span>FACTOR</span>
                                <AddFactorControls
                                    industryCategoryId={activeCategoryId}
                                    disabled={!activeCategoryId}
                                />
                            </div>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        {/* Column 1: Industry list */}
                        <td style={td} valign="top">
                            {loadingIndustries && <div>Loading industries…</div>}
                            {errorIndustries && <div style={{ color: "crimson" }}>Failed to load industries.</div>}
                            {!loadingIndustries && !errorIndustries && (
                                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                    {industries.map((ind) => {
                                        const isActive = ind.industry_id === activeIndustryId;
                                        return (
                                            <li key={ind.industry_id}>
                                                <button
                                                    // when changing industry, clear selected category
                                                    onClick={() => {
                                                        setActiveIndustryId(ind.industry_id === activeIndustryId ? null : ind.industry_id);
                                                        setActiveCategoryId(null);
                                                    }}
                                                    style={{
                                                        display: "block",
                                                        width: "100%",
                                                        textAlign: "left",
                                                        padding: "8px 10px",
                                                        marginBottom: 6,
                                                        borderRadius: 8,
                                                        border: "1px solid #e5e7eb",
                                                        background: isActive ? "#eef2ff" : "#fff",
                                                        cursor: "pointer",
                                                    }}
                                                    title={ind.label}
                                                >
                                                    <div style={{ fontWeight: 600 }}>
                                                        {ind.label}
                                                    </div>
                                                    <div>
                                                        {ind.sbti_name && (
                                                            <span style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                                                                SBTI: {ind.sbti_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        {ind.idx_name && (
                                                            <span style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                                                                IDX: {ind.idx_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        {ind.gri_name && (
                                                            <span style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                                                                GRI: {ind.gri_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                    {industries.length === 0 && <li>No industries.</li>}
                                </ul>
                            )}
                        </td>

                        {/* Column 2: Category list for active industry */}
                        <td style={{ ...td, overflow: "hidden" }} valign="top">
                            <CategoryList
                                industryId={activeIndustryId}
                                activeCategoryId={activeCategoryId}
                                onSelectCategory={(id) => setActiveCategoryId(id)}
                            />
                        </td>

                        {/* Column 3: Factor */}
                        <td style={{ ...td, overflow: "hidden" }} valign="top">
                            <FactorPanel industryCategoryId={activeCategoryId} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

/** ------------ Category list (middle column) ------------ */
function CategoryList({ industryId, activeCategoryId, onSelectCategory }) {
    const qc = useQueryClient();
    const { data: rows = [], isLoading, isError } = useQuery({
        queryKey: ["defaultCategories", industryId],
        queryFn: () => getDefaultEmissionCategories(industryId),
        enabled: !!industryId,
    });

    const deleteMutation = useMutation({
        mutationFn: deleteDefaultEmissionCategory, // expects { id }
        onSuccess: () => qc.invalidateQueries({ queryKey: ["defaultCategories", industryId] }),
    });

    if (isLoading) return <div>Loading categories…</div>;
    if (isError) return <div style={{ color: "crimson" }}>Failed to load categories.</div>;

    return (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {rows.map((r) => {
                const name = r?.category?.name ?? "-";
                const scope = r?.category?.scope ?? null;
                const isActive = r.id === activeCategoryId;
                return (
                    <li
                        key={r.id}
                        onClick={() => onSelectCategory(r.id)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: "8px 10px",
                            marginBottom: 6,
                            background: isActive ? "#eef2ff" : "#fff",
                            cursor: "pointer",
                        }}
                        title="Click to show factors"
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ fontWeight: 600 }}>{name}</div>
                            <ScopeBadge scope={scope} />
                        </div>

                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // don't change selection on delete click
                                deleteMutation.mutate({ id: r.id });
                            }}
                            disabled={deleteMutation.isLoading}
                            style={{
                                padding: "4px 10px",
                                border: "1px solid #ef4444",
                                color: "#ef4444",
                                background: "#fff",
                                borderRadius: 6,
                                cursor: "pointer",
                            }}
                            title="Remove from this industry"
                        >
                            {deleteMutation.isLoading ? "Deleting…" : "Delete"}
                        </button>
                    </li>
                );
            })}
            {rows.length === 0 && (
                <li style={{ color: "#6b7280" }}>No categories yet for this industry.</li>
            )}
        </ul>
    );
}


/** ------------ Add controls in Category header ------------ */
function AddCategoryControls({ activeIndustryId, disabled }) {
    const qc = useQueryClient();

    const [input, setInput] = useState("");         // what user types / shows
    const [query, setQuery] = useState("");         // debounced search term
    const [open, setOpen] = useState(false);        // show/hide dropdown
    const [selected, setSelected] = useState(null); // { id, label, name, scope, nameId }

    // Debounce input -> query (250ms)
    useEffect(() => {
        const t = setTimeout(() => setQuery(input.trim()), 250);
        return () => clearTimeout(t);
    }, [input]);

    // Fetch options when open and query >= 2 chars
    const { data: options = [], isFetching } = useQuery({
        queryKey: ["emissionCategorySearch", query],
        queryFn: () => searchEmissionCategories(query),
        enabled: open && !disabled && query.length >= 2,
        staleTime: 60_000,
        keepPreviousData: true,
    });

    // Reset when industry changes
    useEffect(() => {
        setSelected(null);
        setInput("");
        setQuery("");
        setOpen(false);
    }, [activeIndustryId]);

    const addMutation = useMutation({
        mutationFn: addDefaultEmissionCategory,
        onSuccess: () => {
            if (activeIndustryId) {
                qc.invalidateQueries({ queryKey: ["defaultCategories", activeIndustryId] });
            }
            setSelected(null);
            setInput("");
            setQuery("");
            setOpen(false);
        },
    });

    const canAdd = !!activeIndustryId && !!selected?.id && !disabled;

    return (
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 3 }}>
            {/* Search box */}
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    placeholder='Search category'
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        setSelected(null);   // clear previous selection when typing
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)} // simple close after click
                    disabled={disabled}
                    style={{ padding: "6px 10px", width: 200 }}
                />
                {isFetching && (
                    <span style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 12,
                        color: "#6b7280"
                    }}>
                        …
                    </span>
                )}

                {/* Dropdown list (click to select) */}
                {open && query.length >= 2 && (
                    <div
                        style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "0 0 8px 8px",
                            maxHeight: 240,
                            overflowY: "auto",
                            boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                        }}
                    >
                        {options.length === 0 && !isFetching && (
                            <div style={mutedItem}>No results</div>
                        )}

                        {options.map((opt) => (
                            <div
                                key={opt.id}
                                onMouseDown={(e) => { // mousedown so it fires before input blur
                                    e.preventDefault();
                                    setSelected(opt);
                                    setInput(opt.label);
                                    setOpen(false);
                                }}
                                style={item}
                                title={opt.label}
                            >
                                <div style={{ fontWeight: 600 }}>{opt.name || opt.label}</div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    Scope {opt.scope}{opt.nameId ? ` — ${opt.nameId}` : ""}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add button */}
            <button
                onClick={() => canAdd && addMutation.mutate({
                    industryId: activeIndustryId,
                    emissionCategoryId: Number(selected.id),
                })}
                disabled={!canAdd || addMutation.isLoading}
                style={{
                    padding: "6px 10px",
                    border: "1px solid #10b981",
                    background: "#10b981",
                    color: "#fff",
                    borderRadius: 6,
                    cursor: canAdd ? "pointer" : "not-allowed",
                    fontWeight: 600,
                }}
                title={
                    disabled
                        ? "Select an industry first"
                        : selected?.label
                            ? `Add: ${selected.label}`
                            : "Choose a category"
                }
            >
                {addMutation.isLoading ? "Adding…" : "Add"}
            </button>
        </div>
    );
}

const item = {
    padding: "8px 10px",
    borderTop: "1px solid #e5e7eb",
    cursor: "pointer",
};

const mutedItem = {
    padding: "8px 10px",
    borderTop: "1px solid #e5e7eb",
    color: "#6b7280",
    fontStyle: "italic",
};

/** ------------ Small UI helpers ------------ */
function ScopeBadge({ scope }) {
    if (!scope) return <span style={{ color: "#6b7280" }}>—</span>;
    return (
        <span
            style={{
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: 999,
                background: "#eef2ff",
                border: "1px solid #e5e7eb",
                fontSize: 12,
            }}
            title={`Scope ${scope}`}
        >
            Scope {scope}
        </span>
    );
}

const th = {
    textAlign: "left",
    border: "1px solid #e5e7eb",
    padding: "10px",
    background: "#f3f4f6",
    fontWeight: 700,
};
const td = {
    border: "1px solid #e5e7eb",
    padding: "10px",
    background: "#fff",
};

function FactorPanel({ industryCategoryId }) {
    const qc = useQueryClient();

    const { data: factors = [], isLoading, isError } = useQuery({
        queryKey: ["categoryFactors", industryCategoryId],
        queryFn: () => getCategoryFactors(industryCategoryId),
        enabled: !!industryCategoryId,
    });

    const deleteMutation = useMutation({
        mutationFn: deleteDefaultCategoryFactor, // expects { id }
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ["categoryFactors", industryCategoryId] }),
    });

    if (!industryCategoryId) return <div style={{ color: "#6b7280" }}>Select a category to see factors.</div>;
    if (isLoading) return <div>Loading factors…</div>;
    if (isError) return <div style={{ color: "crimson" }}>Failed to load factors.</div>;

    return (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {factors.map((row) => (
                <li
                    key={row.id}
                    style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "8px 10px",
                        marginBottom: 6,
                        background: "#fff",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, justifyContent: "space-between" }}>
                        <div>
                            <div style={{ fontWeight: 700 }}>{row?.factor?.name ?? "-"} ({row?.factorId})</div>
                            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.35 }}>
                                <EmissionFactorLine data={row?.factor} yearKey="year" digits={2} />
                            </div>
                            <div>
                                {row?.refLabel}
                            </div>
                            <div>
                                {!!row?.units?.length && (
                                    <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                        {row.units.map((u) => (
                                            <span key={u.id} style={{
                                                fontSize: 12,
                                                border: "1px solid #e5e7eb",
                                                borderRadius: 999,
                                                padding: "2px 8px",
                                                background: "#f9fafb"
                                            }}>
                                                {u.name}{u.multiplier ? ` × ${u.multiplier}` : ""}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={() => deleteMutation.mutate({ id: row.id })}
                            disabled={deleteMutation.isLoading}
                            style={{
                                padding: "4px 10px",
                                border: "1px solid #ef4444",
                                color: "#ef4444",
                                background: "#fff",
                                borderRadius: 6,
                                cursor: "pointer",
                            }}
                            title="Remove this factor"
                        >
                            {deleteMutation.isLoading ? "Deleting…" : "Delete"}
                        </button>
                    </div>
                </li>
            ))}
            {factors.length === 0 && <li style={{ color: "#6b7280" }}>No factors.</li>}
        </ul>
    );
}

function AddFactorControls({ industryCategoryId, disabled }) {
    const qc = useQueryClient();

    const [input, setInput] = useState("");
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    // debounce input -> query
    useEffect(() => {
        const t = setTimeout(() => setQuery(input.trim()), 250);
        return () => clearTimeout(t);
    }, [input]);

    // fetch results only when open & query length ok
    const { data: options = [], isFetching } = useQuery({
        queryKey: ["emissionFactorSearch", query],
        queryFn: () => searchEmissionFactors(query),
        enabled: open && !disabled && query.length >= 2,
        staleTime: 60_000,
        keepPreviousData: true,
    });

    // reset when category changes
    useEffect(() => {
        setInput("");
        setQuery("");
        setSelected(null);
        setOpen(false);
    }, [industryCategoryId]);

    const addMutation = useMutation({
        mutationFn: addDefaultCategoryFactor, // { industryCategoryId, factorId }
        onSuccess: () => {
            if (industryCategoryId) {
                qc.invalidateQueries({ queryKey: ["categoryFactors", industryCategoryId] });
            }
            setInput("");
            setQuery("");
            setSelected(null);
            setOpen(false);
        },
    });

    const canAdd = !!industryCategoryId && !!selected?.id && !disabled;

    return (
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    placeholder="Search factor"
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        setSelected(null);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    disabled={disabled}
                    style={{ padding: "6px 10px", width: 280 }}
                />
                {isFetching && (
                    <span style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 12,
                        color: "#6b7280"
                    }}>
                        …
                    </span>
                )}

                {open && query.length >= 2 && (
                    <div
                        style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "0 0 8px 8px",
                            maxHeight: 240,
                            overflowY: "auto",
                            boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                        }}
                    >
                        {options.length === 0 && !isFetching && (
                            <div style={mutedItem}>No results</div>
                        )}

                        {options.map((opt) => (
                            <div
                                key={`${opt.id}-${opt.label}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setSelected(opt);
                                    setInput(opt.label);
                                    setOpen(false);
                                }}
                                style={item}
                                title={opt.label}
                            >
                                <div style={{ fontWeight: 600 }}>{opt.name} ({opt.id})</div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    <EmissionFactorLine data={opt?.factor} yearKey="year" digits={2} />
                                </div>
                            </div>
                        )
                        )}
                    </div>
                )}
            </div>

            <button
                onClick={() => canAdd && addMutation.mutate({ industryCategoryId, factorId: Number(selected.id) })}
                disabled={!canAdd || addMutation.isLoading}
                style={{
                    padding: "6px 10px",
                    border: "1px solid #10b981",
                    background: "#10b981",
                    color: "#fff",
                    borderRadius: 6,
                    cursor: canAdd ? "pointer" : "not-allowed",
                    fontWeight: 600,
                }}
                title={
                    disabled
                        ? "Select a category first"
                        : selected?.label
                            ? `Add: ${selected.label}`
                            : "Choose a factor"
                }
            >
                {addMutation.isLoading ? "Adding…" : "Add"}
            </button>
        </div>
    );
}



